import { useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { deleteQueryParams, updateQueryParam } from 'utils/queryParamsUtil';

import {
  FILTER_PARAM_EXTERNAL_ID,
  FILTER_PARAM_NAME,
  FILTER_PARAM_TYPE,
  FILTER_TYPE_SEPARATOR
} from '../consts';
import { OfferDesignRow } from '../types';
import { filterOffers, getArrayParam, getParam, toggleArrayValue } from '../utils';

export const useOfferDesignFilters = (offers: OfferDesignRow[]) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const externalIdFilter = getParam(params, FILTER_PARAM_EXTERNAL_ID);
  const nameFilter = getParam(params, FILTER_PARAM_NAME);
  const typeFilter = getArrayParam(params, FILTER_PARAM_TYPE);

  const handleExternalIdChange = useCallback(
    (value: string) => updateQueryParam(FILTER_PARAM_EXTERNAL_ID, value, location, navigate),
    [location, navigate]
  );

  const handleNameChange = useCallback(
    (value: string) => updateQueryParam(FILTER_PARAM_NAME, value, location, navigate),
    [location, navigate]
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      const current = getArrayParam(
        new URLSearchParams(location.search),
        FILTER_PARAM_TYPE
      );
      const next = toggleArrayValue(current, value);
      updateQueryParam(FILTER_PARAM_TYPE, next.join(FILTER_TYPE_SEPARATOR), location, navigate);
    },
    [location, navigate]
  );

  const handleClearFilters = useCallback(
    () => deleteQueryParams(
      [FILTER_PARAM_EXTERNAL_ID, FILTER_PARAM_NAME, FILTER_PARAM_TYPE],
      location,
      navigate
    ),
    [location, navigate]
  );

  const filteredOffers = useMemo(
    () => filterOffers(offers, {
      externalId: externalIdFilter,
      name: nameFilter,
      types: typeFilter
    }),
    [offers, externalIdFilter, nameFilter, typeFilter]
  );

  return {
    externalIdFilter,
    nameFilter,
    typeFilter,
    filteredOffers,
    handleExternalIdChange,
    handleNameChange,
    handleTypeChange,
    handleClearFilters
  };
};
